/**
 * Pure form logic for the "Add staff" dialog, split out of the component
 * so it can be unit-tested without a DOM (the frontend test runner uses
 * the `node` environment — no jsdom / @testing-library).
 *
 * The validation here mirrors the backend invariants in
 * admin/service.py::create_staff_user and admin/schemas.py::CreateStaffRequest
 * so the operator gets immediate feedback; the server stays the source of
 * truth and re-validates everything.
 */
import { EMAIL_RE } from '../../lib/email';
import { isMfaCode } from '../../lib/mfaApproval';
import type {
  CreateStaffBody,
  ProposeStaffChangeBody,
  UserRole,
} from '../../services/admin.service';

export type CreatableStaffRole = Extract<UserRole, 'agent' | 'manager' | 'super_admin'>;

/** A manager or a super admin is created by two super admins: this one proposes, a different
 *  one confirms (#326). An agent is created at once. */
export function creationNeedsSecondAdmin(role: CreatableStaffRole): boolean {
  return role !== 'agent';
}

/** Raw dialog state. Empty strings mean "not provided" for the optional
 *  fields (timezone, managerId). */
export interface CreateStaffForm {
  firstName: string;
  lastName: string;
  email: string;
  role: CreatableStaffRole;
  timezone: string;
  managerId: string;
  reason: string;
  /** The acting super_admin's current authenticator code. */
  mfaCode: string;
}

export function emptyCreateStaffForm(): CreateStaffForm {
  return {
    firstName: '',
    lastName: '',
    email: '',
    role: 'agent',
    timezone: '',
    managerId: '',
    reason: '',
    mfaCode: '',
  };
}

/** Human-readable validation errors. Empty array = ready to submit. */
export function createStaffFormErrors(form: CreateStaffForm): string[] {
  const errors: string[] = [];
  if (!form.firstName.trim()) errors.push('First name is required.');
  if (!form.lastName.trim()) errors.push('Last name is required.');
  if (!EMAIL_RE.test(form.email.trim())) errors.push('A valid email is required.');
  if (form.reason.trim().length < 4) errors.push('Reason must be at least 4 characters.');
  if (!isMfaCode(form.mfaCode)) errors.push('Enter the 6-digit code from your authenticator app.');
  // manager_id is only meaningful for agents — managers have no reporting
  // manager of their own in this model.
  if (form.managerId && form.role !== 'agent') {
    errors.push('A reporting manager can only be assigned to an agent.');
  }
  return errors;
}

/** True when the form is safe to POST (no client-side errors). */
export function isCreateStaffSubmittable(form: CreateStaffForm): boolean {
  return createStaffFormErrors(form).length === 0;
}

/** Map the form to the body that creates an AGENT at once. Trims everything, lowercases the
 *  email to match the backend's case-insensitive storage (so the later Google login match
 *  holds) and drops empty optional fields. */
export function toCreateStaffBody(form: CreateStaffForm): CreateStaffBody {
  const body: CreateStaffBody = {
    email: form.email.trim().toLowerCase(),
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    role: 'agent',
    reason: form.reason.trim(),
    mfa_code: form.mfaCode,
  };
  const tz = form.timezone.trim();
  if (tz) body.timezone = tz;
  if (form.managerId) body.manager_id = form.managerId;
  return body;
}

/** Map the form to the proposal of a manager or a super admin, which a different super admin
 *  confirms. Same trimming as an agent; a reporting manager is never part of it. */
export function toCreateProposalBody(form: CreateStaffForm): ProposeStaffChangeBody {
  const body: ProposeStaffChangeBody = {
    kind: 'create',
    email: form.email.trim().toLowerCase(),
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    new_role: form.role,
    reason: form.reason.trim(),
    mfa_code: form.mfaCode,
  };
  const tz = form.timezone.trim();
  if (tz) body.timezone = tz;
  return body;
}
